import {handleActions} from 'redux-actions';
import {Record, List} from 'immutable';
import moment from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {SortDirection} from 'web-app/util/sorting';
import LocalStorage from 'web-app/services/local-storage';

import ActionTypes from './action-types';
import Constants from './constants';
import {roomPlanningParametersKey} from './save-latest-stat-in-local-storage';

const State = Record({
    isLoading: true,
    fetchedDate: null,
    weekOfDate: moment().format('YYYY-MM-DD'),
    days: List(),
    attendanceInfo: List(),
    showAllergies: false,
    showDietaryRemarks: false,
    ageGroups: List(),
    groups: List(),
    behaviors: new BehaviorsCollection(),
    selectedAgeGroup: '',
    selectedGroup: '',
    selectedDay: 0,
    selectedDisplayMode: Constants.DISPLAY_MODE.DETAILED_BREAKDOWN,
    selectedTag: '',
    selectedSortOption: Constants.SORTING.BY_NAME,
    totals: Record(),
    showChildSignatureFields: false,
    showStaffSignatureFields: false,
    groupBy: 'GROUP',
});

const Day = Record({
    day: null,
    intervals: List(),
});

const Interval = Record({
    from: '',
    to: '',
});

const intervalToImmutable = interval => new Interval(interval);

const dayToImmutable = day =>
    new Day({
        ...day,
        intervals: day.intervals ? List(day.intervals.map(intervalToImmutable)) : List(),
    });

const AttendanceInfoItem = Record({
    group: null,
    children: List(),
    expectedChildren: List(),
    maxChildren: List(),
    staffRequired: List(),
    showStaffRequired: true,
    timeSlotCount: null,
});

const Group = Record({
    title: '',
    capacity: null,
    staffRatio: null,
    lower: null,
    upper: null,
    id: '',
    showChildren: false,
    inMultipleAgeGroups: false,
    type: 'GROUP',
});

const Child = Record({
    name: '',
    image: '',
    allergies: '',
    dietaryRemarks: '',
    childId: '',
    age: '',
    birthday: '',
    days: List(),
    fitsInAgeGroup: true,
});

const childToImmutable = child =>
    new Child({
        name: child.name,
        image: child.image,
        allergies: child.allergies,
        dietaryRemarks: child.dietaryRemarks,
        childId: child.childId,
        birthday: child.birthday,
        fitsInAgeGroup: child.fitsInAgeGroup,
        inMultipleAgeGroups: child.inMultipleAgeGroups,
        age: child.age,
        days: List(child.days.map(day => List(day))),
    });

const sortByBirthday = direction => (child1, child2) => {
    if (!child1.birthday) {
        return 1;
    } else if (!child2.birthday) {
        return -1;
    } else {
        return child1.birthday.localeCompare(child2.birthday) * (direction === SortDirection.ASC ? -1 : 1);
    }
};

const sorters = {
    [Constants.SORTING.BY_ASCENDING_AGE]: sortByBirthday(SortDirection.ASC),
    [Constants.SORTING.BY_DESCENDING_AGE]: sortByBirthday(SortDirection.DESC),
    [Constants.SORTING.BY_NAME]: (child1, child2) => {
        return child1.name.localeCompare(child2.name);
    },
};

const sortChildren = (children, sorting) => {
    return children.sort(sorters[sorting]);
};

const attendanceInfoToImmutable = selectedSortOption => attendanceInfoItem => {
    let group = null;
    if (attendanceInfoItem.group) {
        group = new Group({id: attendanceInfoItem.group.groupId, type: 'GROUP', ...attendanceInfoItem.group});
    } else if (attendanceInfoItem.ageGroup) {
        group = new Group({
            id: attendanceInfoItem.ageGroup.ageGroupId,
            type: 'AGE_GROUP',
            ...attendanceInfoItem.ageGroup,
        });
    } else if (attendanceInfoItem.tag) {
        group = new Group({id: attendanceInfoItem.tag.tagId, type: 'TAG', ...attendanceInfoItem.tag});
    }

    return new AttendanceInfoItem({
        group,
        expectedChildren: List(
            attendanceInfoItem.expectedChildren.map(attendanceDayChildrenSummary => List(attendanceDayChildrenSummary)),
        ),
        maxChildren: List(
            attendanceInfoItem.maxChildren.map(attendanceDayChildrenSummary => List(attendanceDayChildrenSummary)),
        ),
        showStaffRequired:
            attendanceInfoItem.staffRequired &&
            attendanceInfoItem.staffRequired[0] &&
            attendanceInfoItem.staffRequired[0][0] !== null,
        staffRequired: List(
            attendanceInfoItem.staffRequired.map(attendanceDayStaffSummary => List(attendanceDayStaffSummary)),
        ),
        children: sortChildren(List(attendanceInfoItem.children.map(childToImmutable)), selectedSortOption),
    });
};

export default handleActions(
    {
        [ActionTypes.TOGGLE_CHILDREN]: (state, {payload}) => {
            const key = state.attendanceInfo.findIndex(item => {
                return item.group.id === payload.groupId;
            });

            const newAttendanceInfo = state.attendanceInfo.update(key, item => {
                return item.merge({
                    group: item.group.merge({
                        showChildren: !item.group.showChildren,
                    }),
                });
            });

            return state.merge({
                attendanceInfo: newAttendanceInfo,
            });
        },

        [ActionTypes.FETCH_ROOM_PLANNING]: (state, {payload}) =>
            state.merge({
                selectedDisplayMode: payload.displayMode,
                weekOfDate: payload.date,
                selectedDay: payload.day,
                selectedGroup: payload.groupId,
                selectedAgeGroup: payload.ageGroupId,
                selectedTag: payload.tagId,
                groupBy: payload.groupBy,
                showAllergies: payload.showAllergies,
                showDietaryRemarks: payload.showDietaryRemarks,
                isLoading: true,
            }),

        [ActionTypes.FETCH_ROOM_PLANNING_SUCCESS]: (state, {payload}) => {
            return state.merge({
                fetchedDate: payload.date,
                days: List(payload.roomPlanning.days.map(dayToImmutable)),
                attendanceInfo: List(payload.roomPlanning.buckets).map(
                    attendanceInfoToImmutable(state.selectedSortOption),
                ),
                behaviors: new BehaviorsCollection(payload.roomPlanning.behaviors),
                totals: new AttendanceInfoItem({
                    expectedChildren: List(
                        payload.roomPlanning.totals.expectedChildren.map(childrenSummary => List(childrenSummary)),
                    ),
                    maxChildren: List(
                        payload.roomPlanning.totals.maxChildren.map(maxChildrenSummary => List(maxChildrenSummary)),
                    ),
                    showStaffRequired:
                        payload.roomPlanning.totals.staffRequired &&
                        payload.roomPlanning.totals.staffRequired[0] &&
                        payload.roomPlanning.totals.staffRequired[0][0] !== null,
                    staffRequired: List(
                        payload.roomPlanning.totals.staffRequired.map(staffSummary => List(staffSummary)),
                    ),
                }),
                isLoading: false,
                ageGroups: List(payload.roomPlanning.ageGroupOptions),
            });
        },

        [ActionTypes.FETCH_ROOM_PLANNING_ABORTED]: (state, payload) =>
            state.merge({
                isLoading: false,
                fetchedDate: payload.date,
                days: List(),
                attendanceInfo: List(),
                behaviors: new BehaviorsCollection(),
                totals: Record(),
                ageGroups: List(),
            }),

        [ActionTypes.FETCH_ROOM_PLANNING_FAILED]: (state, payload) =>
            state.merge({
                isLoading: false,
                fetchedDate: payload.date,
                days: List(),
                attendanceInfo: List(),
                behaviors: new BehaviorsCollection(),
                totals: Record(),
                ageGroups: List(),
            }),

        [ActionTypes.TOGGLE_STAFF_SIGNATURE_FIELDS]: (state, {payload}) =>
            state.merge({
                showStaffSignatureFields: payload.show,
            }),

        [ActionTypes.TOGGLE_CHILD_SIGNATURE_FIELDS]: (state, {payload}) =>
            state.merge({
                showChildSignatureFields: payload.show,
                attendanceInfo: state.attendanceInfo.map(item => {
                    return item.merge({
                        group: item.group.merge({
                            showChildren: true,
                        }),
                    });
                }),
            }),

        [ActionTypes.CHANGE_SORTING]: (state, {payload}) => {
            return state.merge({
                selectedSortOption: payload.sorting,
                attendanceInfo: state.attendanceInfo.map(attendanceInfoItem => {
                    return attendanceInfoItem.set(
                        'children',
                        sortChildren(attendanceInfoItem.children, payload.sorting),
                    );
                }),
            });
        },
    },
    (() => {
        const params = JSON.parse(LocalStorage.getFromLocalStorage(roomPlanningParametersKey)) ?? {};
        return new State({
            weekOfDate: params.date,
            groupBy: params.groupBy,
            selectedGroup: params.groupId,
            selectedDay: params.day,
            selectedDisplayMode: params.displayMode,
            selectedAgeGroup: params.ageGroupId,
            selectedSortOption: params.sorting,
            selectedTag: params.tagId,
            showAllergies: params.showAllergies,
            showDietaryRemarks: params.showDietaryRemarks,
        });
    })(),
);
